import {
  CreateProjectVersionInput,
  Project,
  ProjectVersion,
  UpdateProjectVersionInput,
  // FlowDiagram,
} from "../API";
import {
  createProjectVersion,
  updateProject,
  updateProjectVersion,
} from "../graphql/mutations";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import {
  customGetProject,
  customGetProjectVersion,
  customUpdateProjectVersionNodeIds,
} from "../graphql/customQueries";
import Observable from "zen-observable";
import { ProjectBasicInfo } from "../stores/PlatformStore";
import { generateClient } from "aws-amplify/api";
import { getAccessTokenForLambdaAuth } from "./authHelpers";
import { toProjectFontInput } from "../helpers/fontOverrides";
import { encodeFlowDiagramSourceHandles } from "./flowDiagramSourceHandle";

const client = generateClient();

function omitTypenameDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(omitTypenameDeep) as T;
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((result, [key, nestedValue]) => {
      if (key !== "__typename") {
        result[key] = omitTypenameDeep(nestedValue);
      }
      return result;
    }, {} as Record<string, unknown>) as T;
  }

  return value;
}

function omitTypenameWhenLoaded<T>(value: T | undefined): T | undefined {
  if (typeof value === "undefined") {
    return undefined;
  }

  return omitTypenameDeep(value);
}

function omitUndefinedFields<T extends Record<string, unknown>>(input: T): T {
  return Object.entries(input).reduce((result, [key, value]) => {
    if (typeof value !== "undefined") {
      result[key] = value;
    }
    return result;
  }, {} as Record<string, unknown>) as T;
}

function sanitizeFontOverrides(
  fontOverrides: Project["fontOverrides"] | ProjectVersion["fontOverrides"]
) {
  if (typeof fontOverrides === "undefined") {
    return undefined;
  }

  return fontOverrides?.map(toProjectFontInput) ?? null;
}

export async function createVersion(project: Project | ProjectVersion) {
  const input: CreateProjectVersionInput = omitUndefinedFields({
    id: project.id,
    accountOwner: isProjectVersion(project)
      ? project.accountOwner
      : project.account.id,
    settings: project.settings,
    settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    stateJsonSchemaStr: project.stateJsonSchemaStr,
    textToVideoScript: project.textToVideoScript,
    playerVersionToUse: project.playerVersionToUse,
    experiments: omitTypenameWhenLoaded(project.experiments),
    experimentOptimizationTechnique: project.experimentOptimizationTechnique,
    videoParts: omitTypenameWhenLoaded(project.videoParts),
    flowDiagram: encodeFlowDiagramSourceHandles(
      omitTypenameWhenLoaded(project.flowDiagram)
    ),
    fontOverrides: sanitizeFontOverrides(project.fontOverrides),
    playerSettings: omitTypenameWhenLoaded(project.playerSettings),
    fontMap: omitTypenameWhenLoaded(project.fontMap),
  });

  const modifiedCreateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(createProjectVersion, {
        path: ["videoParts"],
        key: "modsArr",
        value: {
          id: true,
          dataStr: true,
          origin: true,
          name: true,
          description: true,
        },
      }),
      {
        path: ["videoParts"],
        key: "fonts",
        value: {
          family: true,
          style: true,
          url: true,
          weight: true,
        },
      }
    ),
    {
      path: [],
      key: "flowDiagram",
      value: {
        nodes: {
          id: true,
          label: true,
          data: true,
          lineupId: true,
          type: true,
        },
        edges: { source: true, target: true, sourceHandle: true },
      },
    }
  )
    .replaceAll("__typename", "")
    .replaceAll(/\b[A-Za-z_][A-Za-z0-9_]*\s*\{\s*\}/g, "");
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { createProjectVersion: projectVersion },
  } = (await client.graphql({
    query: modifiedCreateProjectVersion,
    variables: {
      input,
    },
    authMode: "lambda",
    authToken,
  })) as { data: { createProjectVersion: ProjectVersion } };
  return projectVersion;
}

export async function updateVersion(project: UpdateProjectVersionInput) {
  const input = {
    id: project.id,
    settings: project.settings,
    settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    stateJsonSchemaStr: project.stateJsonSchemaStr,
    videoParts: project.videoParts,
  };
  const query = updateProjectVersion.replaceAll("__typename", "");
  const authToken = await getAccessTokenForLambdaAuth();
  const response = await client.graphql({
    query,
    variables: {
      input,
    },
    authMode: "lambda",
    authToken,
  });
  // @ts-ignore
  return response.data.updateProjectVersion as ProjectVersion;
}
export async function publishVersion(projectVersion: ProjectVersion) {
  const {
    id,
    settings,
    settingsJsonSchemaStr,
    stateJsonSchemaStr,
    videoParts,
    experiments,
    flowDiagram,
    fontOverrides,
  } = projectVersion;

  const input = omitUndefinedFields({
    id,
    settings,
    settingsJsonSchemaStr,
    stateJsonSchemaStr,
    videoParts: omitTypenameWhenLoaded(videoParts),
    experiments: omitTypenameWhenLoaded(experiments),
    publishedAt: new Date().toISOString(),
    flowDiagram: encodeFlowDiagramSourceHandles(
      omitTypenameWhenLoaded(flowDiagram)
    ),
    fontOverrides: sanitizeFontOverrides(fontOverrides),
  });
  const modifiedUpdateProject = insertIntoGraphQlString(
    insertIntoGraphQlString(
      insertIntoGraphQlString(updateProject, {
        path: ["videoParts"],
        key: "modsArr",
        value: {
          id: true,
          dataStr: true,
          origin: true,
          name: true,
          description: true,
        },
      }),
      {
        path: ["videoParts"],
        key: "fonts",
        value: {
          family: true,
          style: true,
          url: true,
          weight: true,
        },
      }
    ),
    {
      path: [],
      key: "flowDiagram",
      value: {
        nodes: {
          id: true,
          label: true,
          data: true,
          lineupId: true,
          type: true,
        },
        edges: {
          source: true,
          target: true,
          sourceHandle: true,
        },
      },
    }
  )
    .replaceAll("__typename", "")
    .replaceAll(/\b[A-Za-z_][A-Za-z0-9_]*\s*\{\s*\}/g, "");
  const authToken = await getAccessTokenForLambdaAuth();
  return (await client.graphql({
    query: modifiedUpdateProject,
    variables: {
      input,
    },
    authMode: "lambda",
    authToken,
  })) as { data: { updateProject: Project } };
}
export async function getPublishedProject(id: string) {
  // Use customGetProject which already includes:
  // - flowDiagram with full nodes and edges structure
  // - videoParts with modsArr
  // - experiments with variants and successFactors
  // - excludes unauthorized fields (formData, renders, notifications)
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: customGetProject,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: Project } };
  return project;
}

export async function getPublishedProjectBasicInfo(id: string) {
  const getProject = `
  query GetProject($id: ID!) {
    getProject(id: $id) {
      id
      title
      updatedAt
      publishedAt
      createdAt
      playerVersionToUse
      thumbS3Url
      experimentOptimizationTechnique
      projectAccountId
    }
  }
`;

  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: getProject,
    variables: {
      id,
    },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: ProjectBasicInfo } };
  return project;
}

export async function getLatestProjectVersion(id: string) {
  // TODO add logic for fetching latest version when we have multiple versions per project id
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProjectVersion: projectVersion },
  } = (await client.graphql({
    query: customGetProjectVersion,
    variables: {
      id,
    },
    authMode: "lambda",
    authToken,
  })) as { data: { getProjectVersion: ProjectVersion } };
  return projectVersion;
}

/**
 * Persists the idempotent node-id normalization that runs on project
 * selection. The caller builds the input with only the fields the rename
 * touched: flowDiagram always, plus videoParts (interactive mods can
 * reference node ids) and nodeLayout (entries are keyed by node id) when
 * they changed.
 */
export async function persistNormalizedNodeIds(
  input: UpdateProjectVersionInput
): Promise<{ updatedAt: string | null }> {
  const authToken = await getAccessTokenForLambdaAuth();
  const response = (await client.graphql({
    query: customUpdateProjectVersionNodeIds,
    variables: { input },
    authMode: "lambda",
    authToken,
  })) as { data?: { updateProjectVersion?: { updatedAt?: string | null } } };
  return { updatedAt: response.data?.updateProjectVersion?.updatedAt ?? null };
}

export async function getProjectChangesObservable(id: string) {
  const subscription = `
  subscription OnUpdateProjectSub($id: ID) {
    onUpdateProjectSub(id: $id) {
      id
      title
      playerVersionToUse
      updatedAt
      createdAt
    }
  }
`;
  const authToken = await getAccessTokenForLambdaAuth();
  const response = await client.graphql({
    query: subscription,
    variables: {
      id,
    },
    authMode: "lambda",
    authToken,
  });
  if (response instanceof Observable) {
    return response as Observable<object>;
  }
  return null;
}
export async function subscribeToProjectVersionChanges(id: string) {}

function isProjectVersion(
  project: Project | ProjectVersion
): project is ProjectVersion {
  return (project as ProjectVersion).accountOwner !== undefined;
}
